import { useEffect, useRef, useState } from "react";
import { message } from "antd";
import { useTranslation } from "react-i18next";
import { useLanguage } from "src/hooks/UseLanguage";
import { useUser } from "src/contexts/UserContext";
import {
  fetchAccountDisplayData,
  updateAccountProfile,
  updateAccountPassword,
  removeLogo,
  fetchTenantCurrency,
  updateTenantCurrency,
} from "src/services/MyAccountAPIs";
import type { SavedProfile } from "../Types/AccountTypes";
import { PROFILE_IMAGE_SIZE } from "../Utils/AccountConstants";
import {
  GetPasswordStrength,
  IsNameValid,
  IsPasswordValid,
} from "../Utils/AccountPassword";
import { IsAdminRole, IsViewerRole } from "../Utils/AccountRoles";
import { GetErrorMessage } from "../Utils/ErrorMessage";
import { ResizeImageToSquare } from "../Utils/ResizeImageToSquare";

export const useAccountSettings = () => {
  const { t, i18n } = useTranslation();
  useLanguage();
  const { user, setUser } = useUser();
  const isAdmin = IsAdminRole();
  const isViewer = IsViewerRole();
  const hasFetched = useRef(false);

  const [profileImage, setProfileImage] = useState<string | undefined>(undefined);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [industry, setIndustry] = useState("");
  const [currency, setCurrency] = useState<string | undefined>(undefined);
  const [supportedCurrencies, setSupportedCurrencies] = useState<string[]>([]);
  const [savedCurrency, setSavedCurrency] = useState<string | undefined>(undefined);
  const [savedProfile, setSavedProfile] = useState<SavedProfile>({
    firstName: "",
    lastName: "",
    websiteUrl: "",
    industry: "",
  });
  const [company, setCompany] = useState("");
  const [contactNumber, setContactNumber] = useState<string | undefined>(undefined);
  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const passwordStrength = GetPasswordStrength(newPassword, t);

  const validateNames = () => {
    let isValid = true;
    if (!IsNameValid(firstName)) {
      setFirstNameError(t("accountSettings.errors.firstNameMin"));
      isValid = false;
    } else {
      setFirstNameError("");
    }
    if (!IsNameValid(lastName)) {
      setLastNameError(t("accountSettings.errors.lastNameMin"));
      isValid = false;
    } else {
      setLastNameError("");
    }
    return isValid;
  };

  const validatePassword = () => {
    if (!IsPasswordValid(newPassword)) {
      setPasswordError(t("accountSettings.errors.passwordRequirements"));
      return false;
    }
    setPasswordError("");
    return true;
  };

  const fetchData = async () => {
    try {
      const data = await fetchAccountDisplayData();
      setEmail(data.email || "");
      setCompany(data.company_name || "");
      setContactNumber(data.phone_number || "");
      setFirstName(data.first_name || "");
      setLastName(data.last_name || "");
      setWebsiteUrl(data.website_url || "");
      setIndustry(data.industry || "");
      setSavedProfile({
        firstName: data.first_name || "",
        lastName: data.last_name || "",
        websiteUrl: data.website_url || "",
        industry: data.industry || "",
      });
      setProfileImage(data.profile_image_url || undefined);
      setUser(prev => ({
        ...prev,
        ...data,
        profile_image_url: data.profile_image_url || undefined,
      }));
      if (data.profile_image_url) {
        localStorage.setItem("profile_image_url", data.profile_image_url);
      } else {
        localStorage.removeItem("profile_image_url");
      }
    } catch (error) {
      console.error("Display data fetch error:", error);
      message.error(
        GetErrorMessage(error, t("accountSettings.errors.fetchDisplayData"))
      );
    }
  };

  useEffect(() => {
    if (hasFetched.current) {
      return;
    }
    hasFetched.current = true;
    fetchData();
    fetchTenantCurrency(i18n.language)
      .then(data => {
        setCurrency(data.currency_code);
        setSavedCurrency(data.currency_code);
        setSupportedCurrencies(data.supported_currencies ?? []);
      })
      .catch(() => message.error(t("accountSettings.errors.fetchCurrency")));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- fetch-once on mount
  }, []);

  const saveCurrency = async () => {
    if (!isAdmin || !currency || currency === savedCurrency) {
      return;
    }
    try {
      const data = await updateTenantCurrency(currency, i18n.language);
      message.success(data.message);
      setSavedCurrency(data.currency_code ?? currency);
    } catch {
      setCurrency(savedCurrency);
      message.error(t("accountSettings.errors.updateCurrency"));
    }
  };

  const isDirty =
    firstName !== savedProfile.firstName ||
    lastName !== savedProfile.lastName ||
    (isAdmin &&
      (websiteUrl !== savedProfile.websiteUrl ||
        industry !== savedProfile.industry ||
        currency !== savedCurrency));

  const handleProfileUpdate = async () => {
    if (!validateNames()) {
      return;
    }
    setIsUpdatingProfile(true);
    try {
      await updateAccountProfile(
        firstName,
        lastName,
        contactNumber || "",
        selectedFile || undefined,
        isAdmin ? websiteUrl : undefined,
        isAdmin ? industry : undefined
      );
      message.success(t("accountSettings.messages.profileUpdated"));
      await fetchData();
      await saveCurrency();
    } catch (err) {
      message.error(GetErrorMessage(err, t("accountSettings.errors.updateProfile")));
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const handlePhotoUpload = async (file: File) => {
    try {
      const resizedFile = await ResizeImageToSquare(file, PROFILE_IMAGE_SIZE);
      setProfileImage(URL.createObjectURL(resizedFile));
      setSelectedFile(resizedFile);

      await updateAccountProfile(
        firstName,
        lastName,
        contactNumber || "",
        resizedFile,
        isAdmin ? websiteUrl : undefined,
        isAdmin ? industry : undefined
      );

      message.success(t("accountSettings.messages.photoUploaded"));
      fetchData();
    } catch (error) {
      console.error(error);
      message.error(t("accountSettings.errors.resizeImage"));
    }
  };

  const handleRemovePhoto = async () => {
    try {
      const userData = JSON.parse(localStorage.getItem("user") || "{}");
      const userId =
        user?.user_id || userData?.user_id || localStorage.getItem("user_id");
      if (!userId) {
        message.error(t("accountSettings.errors.userIdNotFound"));
        return;
      }
      const res = await removeLogo({ user_id: userId });
      if (res.status) {
        message.success(
          res.message || t("accountSettings.messages.logoRemoved")
        );
        setProfileImage(undefined);
        localStorage.removeItem("profile_image_url");
        fetchData();
      } else {
        message.error(res.message || t("accountSettings.errors.removeLogo"));
      }
    } catch {
      message.error(t("accountSettings.errors.removeLogoGeneric"));
    }
  };

  const handlePasswordChange = async () => {
    if (!validatePassword()) {
      return;
    }
    if (newPassword !== confirmPassword) {
      message.error(t("accountSettings.errors.passwordsNoMatch"));
      return;
    }
    setIsUpdatingPassword(true);
    try {
      await updateAccountPassword(currentPassword, newPassword);
      message.success(t("accountSettings.messages.passwordUpdated"));
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      message.error(
        GetErrorMessage(err, t("accountSettings.errors.updatePassword"))
      );
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  const warnAdminOnly = (key: string) => {
    message.warning(t(key));
  };

  return {
    t,
    isAdmin,
    isViewer,
    profileImage,
    firstName,
    setFirstName,
    lastName,
    setLastName,
    email,
    websiteUrl,
    setWebsiteUrl,
    industry,
    setIndustry,
    currency,
    setCurrency,
    supportedCurrencies,
    company,
    contactNumber,
    firstNameError,
    lastNameError,
    isUpdatingProfile,
    isUpdatingPassword,
    currentPassword,
    setCurrentPassword,
    newPassword,
    setNewPassword,
    confirmPassword,
    setConfirmPassword,
    passwordError,
    passwordStrength,
    isDirty,
    handleProfileUpdate,
    handlePhotoUpload,
    handleRemovePhoto,
    handlePasswordChange,
    warnAdminOnly,
  };
};
